import { useEffect, useRef, useState } from 'react';
import { studioConfig, type StudioManifest } from './types';

const THEME_VARS = ['--bg', '--surface', '--surface-soft', '--ink', '--ink-soft', '--primary', '--primary-ink', '--border',
  '--border-strong', '--success', '--success-bg', '--focus', '--radius', '--radius-lg', '--tile-radius', '--font-size-base', '--line'];

export default function StudioPreview({ manifest, code, sample }: { manifest: StudioManifest; code: string; sample: Record<string, unknown> }) {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [height, setHeight] = useState(360);
  const [tally, setTally] = useState({ right: 0, wrong: 0 });
  const [failed, setFailed] = useState<string | null>(null);
  const [run, setRun] = useState(0);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const answers = new Map<string, string | null>();
    let n = 0;
    const reply = (reqId: unknown, value: unknown, error?: string) => frame.contentWindow?.postMessage({ type: 'result', reqId, value, error }, '*');
    const onMessage = (e: MessageEvent) => {
      if (e.source !== frame.contentWindow) return;
      const m = e.data as Record<string, unknown> | null;
      if (!m || typeof m !== 'object') return;
      if (m.type === 'ready') {
        const cs = getComputedStyle(document.documentElement);
        const theme: Record<string, string> = {};
        for (const v of THEME_VARS) theme[v] = cs.getPropertyValue(v).trim();
        theme['--font'] = getComputedStyle(document.body).fontFamily;
        frame.contentWindow?.postMessage({
          type: 'init', code, config: studioConfig(manifest, sample), theme,
          settings: { reduceMotion: true, errorless: false, textPrompt: true, transitionMs: 0 },
        }, '*');
      } else if (m.type === 'register') {
        const id = `preview-${++n}`;
        const a = m.answer as { value?: unknown } | null;
        answers.set(id, a && typeof a.value === 'string' && a.value.trim() ? a.value : null);
        reply(m.reqId, id);
      } else if (m.type === 'respond') {
        const id = String(m.trialId);
        if (!answers.has(id)) { reply(m.reqId, null, 'unknown round'); return; }
        const expected = answers.get(id);
        answers.delete(id);
        const correct = expected == null || expected.toLowerCase() === String(m.response ?? '').trim().toLowerCase();
        setTally((t) => (correct ? { ...t, right: t.right + 1 } : { ...t, wrong: t.wrong + 1 }));
        reply(m.reqId, { correct });
      } else if (m.type === 'rest') {
        reply(m.reqId, true);
      } else if (m.type === 'resize') {
        const h = Number(m.height);
        if (Number.isFinite(h)) setHeight(Math.max(120, Math.min(4000, Math.round(h))));
      } else if (m.type === 'error') {
        setFailed(typeof m.message === 'string' ? m.message : 'error');
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [manifest, code, sample, run]);

  return (
    <div className="studio-preview">
      {failed && <div className="error" role="alert">The game stopped: {failed}</div>}
      <iframe key={run} ref={frameRef} className="g-studio-frame" title={`Try ${manifest.name}`} src="/studio/runtime.html"
        sandbox="allow-scripts" referrerPolicy="no-referrer" style={{ height }} />
      <div className="studio-preview-bar">
        <span className="muted">Try it here. Nothing is recorded. Right: {tally.right} · Not right: {tally.wrong}</span>
        <button type="button" className="btn secondary" onClick={() => { setFailed(null); setTally({ right: 0, wrong: 0 }); setRun((r) => r + 1); }}>Start again</button>
      </div>
    </div>
  );
}
